import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { collection, query, where, onSnapshot, doc, setDoc, increment } from "firebase/firestore";
import { db } from "../../../firebase";
import { serverTimestamp } from "firebase/firestore"; // Import serverTimestamp for timestamping
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Avatar,
} from "@mui/material";

const Fullview = () => {
  const [documentData, setDocumentData] = useState(null);
  const [userData, setUserData] = useState(null); // State for user data
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // Extract uid, contest_id, and position from URL
  const uid = queryParams.get("uid");
  const contestId = queryParams.get("contestid");
  const position = parseInt(queryParams.get("position"), 10);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!contestId || isNaN(position)) {
        console.error("Invalid contest ID or position");
        return;
      }

      try {
        const collectionRef = collection(db, "prizepoll");
        const q = query(
          collectionRef,
          where("contest_id", "==", contestId),
          where("position", "==", position)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          if (!snapshot.empty) {
            snapshot.forEach((doc) => {
              setDocumentData({ id: doc.id, ...doc.data() });
            });
          } else {
            console.log("No matching documents found");
          }
        });

        return unsubscribe; // Return the unsubscribe function
      } catch (error) {
        console.error("Error fetching document:", error);
      }
    };

    const fetchUserData = async () => {
      if (!uid) {
        console.error("Invalid UID");
        return;
      }

      try {
        const userDocRef = doc(db, "users", uid); // Reference to the user's document
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            setUserData({ id: doc.id, ...doc.data() }); // Set user data if the document exists
          } else {
            console.log("No user found");
          }
        });

        return unsubscribe; // Return the unsubscribe function
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchDocument();
    fetchUserData();
  }, [contestId, position, uid]); // Dependency array ensures effect runs when contestId, position, or uid changes

  const handleReleaseCoins = async () => {
    if (!documentData || !uid || documentData.amount <= 0) {
      console.error("Invalid document data or amount");
      return;
    }

    const remarks = `Released ${documentData.amount} coins`; // Custom remarks for the transaction

    try {
      await setDoc(
        doc(db, "wallet", uid), // Use uid for the wallet document reference
        {
          coins: increment(documentData.amount), // Increment the amount field
          remarks: remarks,
          timestamp: serverTimestamp(), // Use server timestamp
        },
        { merge: true } // Use merge to avoid overwriting existing fields
      );

      // Update the prizepoll document to set releaseDone to true
      await setDoc(
        doc(db, "prizepoll", documentData.id), // Reference to the specific document
        {
          releaseDone: true // Set releaseDone to true
        },
        { merge: true } // Use merge to avoid overwriting existing fields
      );

      alert("Coins released successfully!");
    } catch (error) {
      console.error("Error releasing coins:", error);
      alert(`Error releasing coins: ${error.message}`);
    }
  };

  if (!documentData || !userData) {
    return <div>Loading...</div>; // Show a loading message while fetching data
  }

  return (
    <Box sx={{ padding: 2, margin: '20px auto', maxWidth: 800 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6">Document Details</Typography>
            <Avatar
              src={documentData.image}
              alt={documentData.gift_name}
              sx={{ width: 100, height: 100, marginBottom: 2 }} // Set image size
            />
            <Typography variant="h5">{documentData.gift_name}</Typography>
            <Typography><strong>Contest ID:</strong> {documentData.contest_id}</Typography>
            <Typography><strong>Amount:</strong> {documentData.amount}</Typography>
            <Typography><strong>Position:</strong> {documentData.position}</Typography>
            <Typography><strong>User Name:</strong> {documentData.userName || "N/A"}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6">User Details</Typography>
            <Avatar
              src={userData.photoUrl}
              alt={userData.userName}
              sx={{ width: 100, height: 100, marginBottom: 2 }} // Set user image size
            />
            <Typography variant="body1"><strong>User Name:</strong> {userData.userName}</Typography>
            <Typography variant="body1"><strong>Email:</strong> {userData.email}</Typography>
            <Typography variant="body1"><strong>Account No:</strong> {userData.accountNo}</Typography>
            <Typography variant="body1"><strong>Bank Name:</strong> {userData.bankName}</Typography>
            <Typography variant="body1"><strong>IFSC Code:</strong> {userData.ifscCode}</Typography>
            <Typography variant="body1"><strong>UPI ID:</strong> {userData.upiId}</Typography>
            <Typography variant="body1"><strong>Created At:</strong> {userData.createdAt?.toDate().toString()}</Typography> {/* Format timestamp if needed */}
          </Paper>
        </Grid>
      </Grid>

      <Button 
        onClick={handleReleaseCoins}
        variant="contained"
        disabled={documentData.releaseDone} // Disable button if releaseDone is true
        sx={{ marginTop: 2, backgroundColor: documentData.releaseDone ? 'gray' : 'primary.main' }} // Gray color if released
      >
        Release Coin
      </Button>
    </Box>
  );
};

export default Fullview;
